import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/comics";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6" aria-label="主导航">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-xl font-bold text-foreground-950 whitespace-nowrap"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            <span className="text-primary-500 text-2xl w-7 h-7 flex items-center justify-center">
              <i className="ri-book-open-line"></i>
            </span>
            <span>虚空韩漫网</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === "/"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith("/category")
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
                }`}
                aria-expanded={categoryOpen}
                aria-haspopup="true"
              >
                分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryOpen ? "rotate-180" : ""}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200 rounded-lg shadow-lg py-2 min-w-[160px] z-50">
                  {categories.filter((c) => c.slug !== "all").map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => {
                        navigate(`/category/${cat.slug}`);
                        setCategoryOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-150 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === "/about"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
              }`}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200 py-4 px-4 bg-background-50">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === "/"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                首页
              </Link>
              {categories.filter((c) => c.slug !== "all").map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
                    location.pathname === `/category/${cat.slug}`
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === "/about"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}